import ScrollReveal from '@/components/ScrollReveal';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowRight, Bed, Wifi, Wind, UtensilsCrossed, Eye, Droplets, Waves } from 'lucide-react';
import heraFeature from '@/assets/hera-feature.jpg';
import originalZeusPool from '@/assets/original-zeus-pool.jpg';
import originalHeraView from '@/assets/original-hera-view.jpg';
import originalZeusSunset from '@/assets/original-zeus-sunset.jpg';
import originalHeraTerrace from '@/assets/original-hera-terrace.jpg';
import originalZeusCourtyard from '@/assets/original-zeus-courtyard.jpg';

export default function Apartments() {
  const { t } = useLanguage();

  return (
    <div>
      {/* ═══ HERO ═══ */}
      <section className="relative h-[80vh] flex items-end overflow-hidden">
        <img src={originalZeusCourtyard} alt="Stone courtyard at Sivota Royal" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 lg:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 animate-reveal drop-shadow-md">{t('theResidences')}</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white animate-reveal drop-shadow-lg">{t('ourApartments')}</h1>
          <p className="text-white/80 text-lg mt-4 max-w-xl animate-reveal-delay drop-shadow-md">{t('twoExceptionalResidences')}</p>
        </div>
      </section>

      {/* ═══ HERA ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-sm overflow-hidden shadow-lg max-w-7xl mx-auto">
            <div className="image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[600px]">
              <img src={heraFeature} alt="Hera Apartment" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
            <div className="bg-card flex flex-col justify-center p-10 lg:p-16 xl:p-20">
              <p className="text-luxury mb-3 text-accent">{t('theApartments')}</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-5">Hera</h2>
              <div className="w-12 h-px bg-golden mb-6" />
              <p className="text-muted-foreground leading-relaxed mb-4">{t('heraDesc')}</p>
              <p className="text-muted-foreground leading-relaxed mb-8 text-sm">{t('heraExtendedDesc')}</p>
              <div className="grid grid-cols-3 gap-x-6 gap-y-3 mb-10">
                {[
                  { icon: Bed, label: t('twoBedrooms') },
                  { icon: Eye, label: t('seaViews') },
                  { icon: Wifi, label: t('freeWiFi') },
                  { icon: Wind, label: t('ac') },
                  { icon: UtensilsCrossed, label: t('fullKitchen') },
                  { icon: Waves, label: t('terrace') },
                ].map((a, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-muted-foreground text-sm">
                    <a.icon size={16} className="text-golden shrink-0" aria-hidden="true" />
                    <span>{a.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/apartments/hera" className="group inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-all duration-300 self-start">
                {t('exploreHera')} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ ARTISTIC GALLERY STRIP ═══ */}
      <ScrollReveal>
        <div className="grid grid-cols-3 gap-2 section-padding">
          {[
            { src: originalHeraTerrace, alt: 'Hera terrace view' },
            { src: originalZeusCourtyard, alt: 'Zeus courtyard' },
            { src: originalZeusSunset, alt: 'Sunset from property' },
          ].map((img, i) => (
            <div key={i} className="image-zoom aspect-[16/10] rounded-sm overflow-hidden">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover hero-image-bright" loading="lazy" width={600} height={375} />
            </div>
          ))}
        </div>
      </ScrollReveal>

      {/* ═══ ZEUS ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-sm overflow-hidden shadow-lg max-w-7xl mx-auto">
            <div className="bg-card flex flex-col justify-center p-10 lg:p-16 xl:p-20 order-2 lg:order-1">
              <p className="text-luxury mb-3 text-accent">{t('theApartments')}</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-5">Zeus</h2>
              <div className="w-12 h-px bg-golden mb-6" />
              <p className="text-muted-foreground leading-relaxed mb-4">{t('zeusDesc')}</p>
              <p className="text-muted-foreground leading-relaxed mb-8 text-sm">{t('zeusExtendedDesc')}</p>
              <div className="grid grid-cols-3 gap-x-6 gap-y-3 mb-10">
                {[
                  { icon: Bed, label: t('twoBedrooms') },
                  { icon: Droplets, label: t('privatePoolLabel') },
                  { icon: Eye, label: t('panoramicViews') },
                  { icon: Wifi, label: t('freeWiFi') },
                  { icon: Wind, label: t('ac') },
                  { icon: UtensilsCrossed, label: t('fullKitchen') },
                ].map((a, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-muted-foreground text-sm">
                    <a.icon size={16} className="text-golden shrink-0" aria-hidden="true" />
                    <span>{a.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/apartments/zeus" className="group inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-all duration-300 self-start">
                {t('exploreZeus')} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
            <div className="image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[600px] order-1 lg:order-2">
              <img src={originalZeusPool} alt="Zeus Apartment — pool area" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="relative py-28 lg:py-36">
        <img src={originalZeusSunset} alt="" className="absolute inset-0 w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={1080} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
        <div className="relative z-10 text-center section-padding">
          <ScrollReveal>
            <p className="text-white/80 tracking-[0.3em] uppercase text-xs font-sans mb-6">{t('startPlanning')}</p>
            <h2 className="font-serif text-4xl md:text-6xl font-light text-white mb-6 drop-shadow-lg">{t('readyExperienceSivota')}</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://www.booking.com/hotel/gr/sivota-royal.en-gb.html" target="_blank" rel="noopener noreferrer"
                className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">
                {t('bookNow')}
              </a>
              <Link to="/booking" className="border border-white/40 text-white px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">
                {t('sendEnquiry')}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
